import React from 'react';
import { motion } from 'framer-motion';
import { FiShield, FiSearch, FiCode, FiLock, FiDatabase, FiGlobe, FiZap, FiUsers } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';

const Features = () => {
  const features = [
    {
      icon: <FiShield />,
      title: "SCA - Software Composition Analysis",
      description: "Scan your dependencies for known vulnerabilities and get automated fixes through ANA Actions. The most comprehensive dependency security scanning available.",
      capabilities: [
        "Scan third-party libraries and dependencies",
        "Identify known CVEs and security vulnerabilities",
        "Automated fix suggestions with pull requests",
        "License compliance and risk assessment"
      ]
    },
    {
      icon: <FiCode />,
      title: "SAST - Static Application Security Testing",
      description: "Analyze your source code for security vulnerabilities before deployment. Catch security issues early in the development cycle.",
      capabilities: [
        "Deep source code security analysis",
        "Identify injection flaws and XSS vulnerabilities",
        "Authentication and authorization issues",
        "Cryptographic and data validation problems"
      ]
    },
    {
      icon: <FiGlobe />,
      title: "DAST - Dynamic Application Security Testing",
      description: "Test your running applications for security vulnerabilities. Identify runtime security issues and configuration problems.",
      capabilities: [
        "Runtime application security testing",
        "Web application vulnerability scanning",
        "API endpoint security assessment",
        "Configuration and deployment security"
      ]
    },
    {
      icon: <FiLock />,
      title: "Secrets Detection",
      description: "Prevent credential leaks and exposed secrets. Automatically detect API keys, passwords, and other sensitive information in your codebase.",
      capabilities: [
        "Scan for exposed API keys and tokens",
        "Detect hardcoded passwords and credentials",
        "Database connection strings and certificates",
        "Custom secret pattern detection"
      ]
    },
    {
      icon: <FiSearch />,
      title: "Threat Modeling",
      description: "Identify and analyze potential security threats. Systematic approach to understanding attack vectors and security risks in your applications.",
      capabilities: [
        "Systematic threat identification",
        "Attack vector analysis and mapping",
        "Risk assessment and prioritization",
        "Security architecture recommendations"
      ]
    },
    {
      icon: <FiDatabase />,
      title: "SBOM - Software Bill of Materials",
      description: "Track all software components and dependencies. Generate comprehensive software bill of materials for compliance and security tracking.",
      capabilities: [
        "Complete dependency inventory",
        "License and compliance tracking",
        "Vulnerability impact assessment",
        "Supply chain security monitoring"
      ]
    },
    {
      icon: <FiZap />,
      title: "API Security",
      description: "Secure your API endpoints and interfaces. Comprehensive API security testing and vulnerability assessment for REST and GraphQL APIs.",
      capabilities: [
        "REST and GraphQL API security testing",
        "Authentication and authorization testing",
        "Input validation and injection testing",
        "Rate limiting and abuse prevention"
      ]
    },
    {
      icon: <FiUsers />,
      title: "Infrastructure Security",
      description: "Secure your deployment and infrastructure. Scan cloud configurations, container images, and infrastructure as code for security issues.",
      capabilities: [
        "Cloud configuration security scanning",
        "Container image vulnerability assessment",
        "Infrastructure as Code (IaC) security",
        "Kubernetes and Docker security"
      ]
    }
  ];

  const automationFeatures = [
    {
      title: "ANA Actions",
      description: "Automated security fixes and remediation",
      details: [
        "Automatic pull request creation for dependency updates",
        "Smart vulnerability remediation suggestions",
        "Integration with GitHub, Bitbucket, and GitLab",
        "Customizable fix policies and approval workflows"
      ]
    },
    {
      title: "CI/CD Integration",
      description: "Seamless integration with your development workflow",
      details: [
        "GitHub Actions and GitLab CI/CD support",
        "Jenkins, Azure DevOps, and CircleCI integration",
        "Automated security gates and quality checks",
        "Real-time security feedback in pull requests"
      ]
    },
    {
      title: "Real-time Monitoring",
      description: "Continuous security monitoring and alerting",
      details: [
        "Real-time vulnerability notifications",
        "Security posture trending and analytics",
        "Custom alerting rules and thresholds",
        "Integration with Slack, Teams, and email"
      ]
    }
  ];

  const cardClasses = `
    bg-light-card dark:bg-dark-card
    backdrop-blur-sm
    rounded-lg
    border border-light-border dark:border-dark-border
    p-6 cursor-pointer
    transition-all duration-200
    hover:border-primary-500/30 dark:hover:border-primary-500/30
    hover:shadow-lg hover:shadow-primary-500/5 dark:hover:shadow-primary-500/10
  `;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="Security Features" />
        <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
          Explore Ana Security's comprehensive security scanning capabilities that help you build safer, more secure applications.
        </p>

        {/* Core Security Modules */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Core Security Modules
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div key={index} className={cardClasses}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ 
                    scale: 1.02,
                    transition: { duration: 0.2 }
                  }}
                >
                  <div className="text-2xl text-primary-500 dark:text-primary-400 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {feature.description}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
                    {feature.capabilities.map((capability, idx) => (
                      <li key={idx}>{capability}</li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            ))}
          </div>
        </div>

        {/* Automation & Integration Features */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Automation & Integration
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {automationFeatures.map((feature, index) => (
              <div key={index} className={cardClasses}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ 
                    scale: 1.02,
                    transition: { duration: 0.2 }
                  }}
                >
                  <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {feature.description}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    {feature.details.map((detail, idx) => (
                      <li key={idx}>{detail}</li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            ))}
          </div>
        </div>

        {/* Security Metrics & Analytics */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Security Metrics & Analytics
          </h2>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Comprehensive Dashboard
                </h3>
                <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Real-time security posture overview</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Vulnerability trends and analytics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Project-level security metrics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Scan history and progress tracking</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Advanced Reporting
                </h3>
                <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Executive security summaries</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Compliance and audit reports</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Custom report generation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Export to PDF, CSV, and JSON</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Enterprise Features */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Enterprise Security Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Access Control & Governance
              </h3>
              <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                <li>• Role-based access control (RBAC)</li>
                <li>• Single sign-on (SSO) integration</li>
                <li>• Multi-factor authentication (MFA)</li>
                <li>• Audit logging and compliance tracking</li>
              </ul>
            </div>
            <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Scalability & Performance
              </h3>
              <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                <li>• High-performance scanning engines</li>
                <li>• Distributed scanning architecture</li>
                <li>• API rate limiting and throttling</li>
                <li>• Enterprise-grade SLA guarantees</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Continuous Evolution */}
        <div className="mt-12 bg-primary-500/5 dark:bg-primary-500/10 rounded-lg border border-primary-500/20 dark:border-primary-500/20 p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h3 className="text-lg font-semibold mb-2 text-primary-600 dark:text-primary-400">
              Continuous Evolution
            </h3>
            <p className="text-gray-700 dark:text-gray-300">
              Ana Security's features are continuously expanding and improving. We regularly add new security modules, 
              enhance existing capabilities, and integrate with the latest security tools and frameworks. Check back regularly 
              for updates and new capabilities to enhance your security workflow.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default Features;
